export type FeatureFlags = {
  developerCenter: boolean
  allowlistOnly: boolean
  staticDeploy: boolean
  functions: boolean
  customDomains: boolean
  aiGrants: boolean
  publicSignup: boolean
  gitWebhooks: boolean
}

export type PlatformStatus = {
  service: string
  version: string
  platformDomain: string
  features: FeatureFlags
  integrations: {
    sso: boolean
    edgeAuth: boolean
    buildRunner: boolean
    deployController: boolean
    domainController: boolean
    grantGateway: boolean
    vault: boolean
    newApi: boolean
  }
  integrationHealth: {
    buildRunner: IntegrationReadiness
    deployController: IntegrationReadiness
  }
  limits: {
    sourceArchiveBytes: number
    extractedBytes: number
    maxFileBytes: number
    extractedFiles: number
    compressionRatio: number
    maxProjectsPerAccount: number
    maxDeploymentsPerProject: number
    maxSourceBytesPerAccount: number
    maxConcurrentBuildsPerAccount: number
    maxDeploymentsPerMinute: number
    deletedDeploymentRetentionDays: number
    functionBuildMemoryMb: number
    functionBuildTimeoutSeconds: number
    functionImageBytes: number
    functionActiveRevisions: number
    functionRolloutSurge: number
    functionMemoryMb: number
    functionCpuMillis: number
  }
  authentication: {
    mode: 'development' | 'edge' | 'sso'
    authorizeUrl: string | null
    clientId: string | null
    callbackPath: string
  }
  storage: {
    driver: 'local' | 's3'
    singleNode: boolean
  }
  functionBoundary: {
    buildNetwork: string
    buildPublicEgress: boolean
    runtimePublicEgress: boolean
    runtimeEgressMode: 'disabled' | 'allowlisted-http-proxy'
    isolation: 'development-docker' | 'gvisor'
    rootlessRequired: boolean
  }
}

export type IntegrationReadiness = {
  configured: boolean
  ready: boolean
  status: 'ready' | 'unconfigured' | 'unavailable'
}

export type DeveloperAccount = {
  publicId: string
  displayName: string
  role: number
  language: string
  developerAccess: boolean
  identityProvider: 'computinger' | 'edge-alpha'
  administrator: boolean
}

export type AgentAccessToken = {
  publicId: string
  name: string
  tokenPrefix: string
  scopes: Array<'project:read' | 'deployment:write' | 'environment:write'>
  expiresAt: string
  lastUsedAt: string | null
  revokedAt: string | null
  createdAt: string
}

export type AdminControlPlane = {
  overview: {
    accounts: number
    approvedAccounts: number
    projects: number
    pendingBuilds: number
    runningBuilds: number
    readyRuntimeNodes: number
    openIncidents: number
  }
  accounts: Array<{
    publicId: string
    newApiUserId: string | null
    identityProvider: 'computinger' | 'edge-alpha'
    displayName: string
    role: number
    language: string
    status: string
    developerAccess: boolean
    accessPolicy: 'inherited' | 'granted' | 'revoked'
    updatedAt: string
  }>
  buildJobs: Array<{
    publicId: string
    state: string
    workerId: string | null
    attempt: number
    lastError: string | null
    createdAt: string
    deploymentPublicId: string
    kind: string
    projectPublicId: string
    projectName: string
  }>
  runtimeNodes: Array<{
    publicId: string
    name: string
    region: string
    isolationBackend: string
    state: string
    capacityCpuMillis: number
    capacityMemoryMb: number
    allocatedCpuMillis: number
    allocatedMemoryMb: number
    lastHeartbeatAt: string
  }>
  revisions: Array<{
    publicId: string
    state: string
    deploymentPublicId: string
    kind: string
    projectName: string
    runtimeNodeName: string | null
    createdAt: string
  }>
  domains: Array<{
    publicId: string
    hostname: string
    kind: 'platform' | 'custom'
    state: string
    projectName: string
    certificateState: string | null
    certificateIssuer: string | null
    certificateNotAfter: string | null
    certificateLastError: string | null
  }>
  grants: Array<{
    publicId: string | null
    period: string
    limitQuota: string
    usedQuota: string
    projectPublicId: string
    projectName: string
    credentialStatus: string | null
    credentialLastError: string | null
    credentialLastReconciledAt: string | null
  }>
  incidents: Array<{
    publicId: string
    severity: string
    state: string
    summary: string
    runtimeNodeName: string | null
    deploymentPublicId: string | null
    createdAt: string
  }>
  auditEvents: Array<{
    publicId: string
    action: string
    resourceType: string
    resourceId: string
    actorDisplayName: string | null
    createdAt: string
  }>
  capabilities: {
    functions: boolean
    customDomains: boolean
    aiGrants: boolean
    buildRunner: boolean
    deployController: boolean
    vault: boolean
    grantGateway: boolean
  }
  policies: {
    grantMaxProjectMonthlyQuota: number
  }
}

export type Project = {
  publicId: string
  name: string
  slug: string
  status: string
  createdAt: string
  updatedAt: string
  deletedAt?: string | null
  environmentPublicId: string
  platformHostname: string
  activeDeploymentPublicId: string | null
  activeDeploymentState: string | null
  deploymentCount?: number
}

export type Deployment = {
  publicId: string
  state: string
  kind: string
  manifest: Record<string, unknown>
  failureCode: string | null
  failureMessage: string | null
  createdAt: string
  updatedAt: string
  readyAt: string | null
  deletedAt: string | null
  deletedByAccountId: string | null
  deletionReason: string | null
  artifactObjectKey: string | null
  imageDigest: string | null
  sourceDigest: string
  sourceSizeBytes: string
  sourceKind: 'upload' | 'git'
  gitRef: string | null
  gitCommitSha: string | null
  gitRepositoryOwner: string | null
  gitRepositoryName: string | null
}

export type ProjectRepository = {
  publicId: string
  provider: 'github'
  repositoryOwner: string
  repositoryName: string
  defaultBranch: string
  visibility: 'public'
  automaticDeployments: boolean
  productionBranch: string
  webhookAvailable: boolean
  webhookUrl: string
  lastDeliveryPublicId: string | null
  lastDeliveryEvent: string | null
  lastDeliveryEventAction: string | null
  lastDeliveryStatus: 'received' | 'processing' | 'accepted' | 'ignored' | 'failed' | null
  lastDeliveryGitRef: string | null
  lastDeliveryCommitSha: string | null
  lastDeliveryPullRequestNumber: number | null
  lastDeliveryResultCode: string | null
  lastDeliveryResultMessage: string | null
  lastDeliveryReceivedAt: string | null
  lastDeliveryProcessedAt: string | null
  lastDeliveryDeploymentPublicId: string | null
  createdAt: string
  updatedAt: string
}

export type GitPreview = {
  publicId: string
  pullRequestNumber: number
  sourceRepositoryOwner: string
  sourceRepositoryName: string
  headRef: string
  headCommitSha: string
  state: 'active' | 'closed'
  openedAt: string
  updatedAt: string
  closedAt: string | null
  environmentPublicId: string
  hostname: string
  domainState: string
  lastDeploymentPublicId: string | null
  lastDeploymentState: string | null
  lastDeploymentKind: string | null
  lastDeploymentFailureCode: string | null
  lastDeploymentFailureMessage: string | null
  lastDeliveryStatus: 'received' | 'processing' | 'accepted' | 'ignored' | 'failed' | null
  lastDeliveryResultCode: string | null
  lastDeliveryResultMessage: string | null
  activeDeploymentPublicId: string | null
  activeDeploymentState: string | null
}

export type RepositoryWebhookCredential = {
  enabled: true
  branch: string
  url: string
  secret: string | null
}

export type GitHubBranch = {
  name: string
  protected: boolean
  commitSha: string
}

export type Domain = {
  publicId: string
  hostname: string
  kind: 'platform' | 'custom'
  state: string
  verifiedAt: string | null
  createdAt: string
  updatedAt: string
  routingTarget: string | null
  acmeChallengeTarget: string | null
  lastOwnershipCheckAt: string | null
  ownershipFailureCount: number
  lastOwnershipError: string | null
  certificateState: string | null
  certificateIssuer: string | null
  certificateNotAfter: string | null
  certificateLastError: string | null
}

export type DeploymentLog = {
  sequence: string
  stream: 'system' | 'stdout' | 'stderr'
  message: string
  createdAt: string
}

export type EnvironmentVariable = {
  name: string
  value: string
  updatedAt: string
}

export type SecretReference = {
  publicId: string
  name: string
  vaultRef: string
  version: number
  updatedAt: string
}

export type DeviceAuthorization = {
  publicId: string
  clientName: string
  clientVersion: string
  state: 'pending' | 'approved' | 'denied' | 'consumed' | 'expired'
  expiresAt: string
  approvedAt: string | null
}

export class ApiClientError extends Error {
  readonly code: string
  readonly status: number
  readonly requestId?: string

  constructor(status: number, code: string, message: string, requestId?: string) {
    super(message)
    this.name = 'ApiClientError'
    this.status = status
    this.code = code
    this.requestId = requestId
  }
}

const developmentUserId = import.meta.env.VITE_DEV_AUTH_USER_ID ?? '10001'

async function apiFetch<T>(
  path: string,
  init: RequestInit = {},
  requiresAuthentication = true
): Promise<T> {
  const headers = new Headers(init.headers)
  headers.set('Accept', 'application/json')
  if (import.meta.env.DEV) headers.set('X-Computinger-User-Id', developmentUserId)
  const response = await fetch(`/api/v1${path}`, {
    ...init,
    credentials: 'include',
    headers,
  })
  const payload = (await response.json().catch(() => null)) as
    | { error?: { code?: string; message?: string; requestId?: string } }
    | null
  if (!response.ok) {
    const error = new ApiClientError(
      response.status,
      payload?.error?.code ?? 'request_failed',
      payload?.error?.message ?? 'Request failed',
      payload?.error?.requestId
    )
    if (requiresAuthentication && response.status === 401) {
      window.dispatchEvent(new Event('computinger:authentication-required'))
    }
    throw error
  }
  return payload as T
}

export const api = {
  status: () => apiFetch<PlatformStatus>('/status', {}, false),
  me: () =>
    apiFetch<{
      account: DeveloperAccount
    }>('/me'),
  accessTokens: () =>
    apiFetch<{ items: AgentAccessToken[] }>('/access-tokens'),
  createAccessToken: (name: string) =>
    apiFetch<{ token: string; accessToken: AgentAccessToken }>('/access-tokens', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name,
        expiresInDays: 90,
        scopes: ['project:read', 'deployment:write', 'environment:write'],
      }),
    }),
  revokeAccessToken: (tokenId: string) =>
    apiFetch<{ revoked: boolean }>(`/access-tokens/${encodeURIComponent(tokenId)}`, {
      method: 'DELETE',
    }),
  exchangeSsoCode: (input: {
    code: string
    codeVerifier: string
    redirectUri: string
  }) =>
    apiFetch<{ account: DeveloperAccount }>(
      '/auth/exchange',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      },
      false
    ),
  logout: () =>
    apiFetch<{ signedOut: boolean }>('/auth/logout', { method: 'POST' }, false),
  deviceAuthorization: (userCode: string) =>
    apiFetch<{ authorization: DeviceAuthorization }>(
      `/device-authorizations/${encodeURIComponent(userCode)}`
    ),
  approveDeviceAuthorization: (userCode: string) =>
    apiFetch<{ authorization: DeviceAuthorization }>(
      `/device-authorizations/${encodeURIComponent(userCode)}/approve`,
      { method: 'POST' }
    ),
  denyDeviceAuthorization: (userCode: string) =>
    apiFetch<{ authorization: DeviceAuthorization }>(
      `/device-authorizations/${encodeURIComponent(userCode)}/deny`,
      { method: 'POST' }
    ),
  projects: (includeArchived = false) =>
    apiFetch<{ items: Project[] }>(
      includeArchived ? '/projects?includeArchived=true' : '/projects'
    ),
  project: (projectId: string) =>
    apiFetch<{ project: Project }>(`/projects/${projectId}`),
  createProject: (input: { name: string; slug: string; platformSubdomain?: string }) =>
    apiFetch<{ project: Project }>('/projects', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    }),
  suspendProject: (projectId: string) =>
    apiFetch<{ project: Project }>(`/projects/${projectId}/suspend`, { method: 'POST' }),
  resumeProject: (projectId: string) =>
    apiFetch<{ project: Project }>(`/projects/${projectId}/resume`, { method: 'POST' }),
  deleteProject: (projectId: string) =>
    apiFetch<{ project: { publicId: string; status: string; deletedAt: string } }>(
      `/projects/${projectId}`,
      { method: 'DELETE' }
    ),
  restoreProject: (projectId: string) =>
    apiFetch<{ project: Project }>(`/projects/${projectId}/restore`, { method: 'POST' }),
  deployments: (projectId: string) =>
    apiFetch<{ items: Deployment[] }>(`/projects/${projectId}/deployments`),
  repository: (projectId: string) =>
    apiFetch<{ repository: ProjectRepository | null }>(`/projects/${projectId}/repository`),
  previews: (projectId: string) =>
    apiFetch<{ items: GitPreview[] }>(`/projects/${projectId}/previews`),
  connectRepository: (projectId: string, repository: string) =>
    apiFetch<{ repository: ProjectRepository }>(`/projects/${projectId}/repository`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ repository }),
    }),
  disconnectRepository: (projectId: string) =>
    apiFetch<{ disconnected: boolean }>(`/projects/${projectId}/repository`, {
      method: 'DELETE',
    }),
  repositoryBranches: (projectId: string) =>
    apiFetch<{ items: GitHubBranch[] }>(`/projects/${projectId}/repository/branches`),
  configureRepositoryWebhook: (projectId: string, branch: string) =>
    apiFetch<{ webhook: RepositoryWebhookCredential }>(
      `/projects/${projectId}/repository/webhook`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ branch }),
      }
    ),
  disableRepositoryWebhook: (projectId: string) =>
    apiFetch<{ disabled: boolean }>(`/projects/${projectId}/repository/webhook`, {
      method: 'DELETE',
    }),
  rotateRepositoryWebhookSecret: (projectId: string) =>
    apiFetch<{ webhook: RepositoryWebhookCredential }>(
      `/projects/${projectId}/repository/webhook/rotate`,
      { method: 'POST' }
    ),
  deployRepository: (projectId: string, reference?: string) =>
    apiFetch<{ deployment: Deployment }>(`/projects/${projectId}/repository/deployments`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Idempotency-Key': crypto.randomUUID(),
      },
      body: JSON.stringify({ reference }),
    }),
  deploy: (projectId: string, archive: File) =>
    apiFetch<{ deployment: Deployment }>(`/projects/${projectId}/deployments`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/zip',
        'Idempotency-Key': crypto.randomUUID(),
      },
      body: archive,
    }),
  rollback: (projectId: string, deploymentId: string) =>
    apiFetch<{ deployment: Deployment }>(
      `/projects/${projectId}/deployments/${deploymentId}/rollback`,
      { method: 'POST' }
    ),
  cancelDeployment: (projectId: string, deploymentId: string) =>
    apiFetch<{ deployment: { publicId: string; state: string } }>(
      `/projects/${projectId}/deployments/${deploymentId}/cancel`,
      { method: 'POST' }
    ),
  deleteDeployment: (projectId: string, deploymentId: string) =>
    apiFetch<{ deployment: { publicId: string; deleted: boolean } }>(
      `/projects/${projectId}/deployments/${deploymentId}`,
      { method: 'DELETE' }
    ),
  restoreDeployment: (projectId: string, deploymentId: string) =>
    apiFetch<{ deployment: { publicId: string; state: string; deleted: boolean } }>(
      `/projects/${projectId}/deployments/${deploymentId}/restore`,
      { method: 'POST' }
    ),
  retryDeployment: (projectId: string, deploymentId: string) =>
    apiFetch<{ deployment: { publicId: string; state: string } }>(
      `/projects/${projectId}/deployments/${deploymentId}/retry`,
      { method: 'POST' }
    ),
  logs: (projectId: string, deploymentId: string) =>
    apiFetch<{ items: DeploymentLog[] }>(
      `/projects/${projectId}/deployments/${deploymentId}/logs`
    ),
  runtimeMetrics: (projectId: string) =>
    apiFetch<{
      periodHours: number
      metrics: {
        requests: number
        errors: number
        timeouts: number
        averageDurationMs: number
        p95DurationMs: number
      }
    }>(`/projects/${projectId}/runtime-metrics`),
  domains: (projectId: string) =>
    apiFetch<{ items: Domain[] }>(`/projects/${projectId}/domains`),
  createDomain: (projectId: string, hostname: string) =>
    apiFetch<{
      domain: Domain
      verification: {
        recordType: string
        recordName: string
        recordValue: string
        expiresInSeconds: number
      }
      routing: { recordType: string; recordName: string; recordValue: string }
      tlsDelegation: { recordType: string; recordName: string; recordValue: string }
    }>(`/projects/${projectId}/domains`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ hostname }),
    }),
  verifyDomain: (projectId: string, domainId: string, token: string) =>
    apiFetch<{ domain: Domain }>(`/projects/${projectId}/domains/${domainId}/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    }),
  rotateDomainVerification: (projectId: string, domainId: string) =>
    apiFetch<{
      domain: Domain
      verification: {
        recordType: string
        recordName: string
        recordValue: string
        expiresInSeconds: number
      }
    }>(`/projects/${projectId}/domains/${domainId}/verification`, {
      method: 'POST',
    }),
  recheckDomain: (projectId: string, domainId: string) =>
    apiFetch<{ domain: Domain }>(
      `/projects/${projectId}/domains/${domainId}/recheck`,
      { method: 'POST' }
    ),
  removeDomain: (projectId: string, domainId: string) =>
    apiFetch<{ domain: Domain }>(`/projects/${projectId}/domains/${domainId}`, {
      method: 'DELETE',
    }),
  environmentVariables: (projectId: string) =>
    apiFetch<{ items: EnvironmentVariable[] }>(
      `/projects/${projectId}/environment-variables`
    ),
  saveEnvironmentVariables: (
    projectId: string,
    variables: Record<string, string>
  ) =>
    apiFetch<{ saved: boolean; count: number }>(
      `/projects/${projectId}/environment-variables`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ variables }),
      }
    ),
  secrets: (projectId: string) =>
    apiFetch<{ items: SecretReference[]; vaultConfigured: boolean }>(
      `/projects/${projectId}/secrets`
    ),
  saveSecret: (projectId: string, name: string, vaultRef: string) =>
    apiFetch<{ secret: SecretReference }>(`/projects/${projectId}/secrets/${name}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ vaultRef }),
    }),
  revokeSecret: (projectId: string, name: string) =>
    apiFetch<{ revoked: boolean; secret: Pick<SecretReference, 'publicId' | 'name' | 'version'> }>(
      `/projects/${projectId}/secrets/${encodeURIComponent(name)}`,
      { method: 'DELETE' }
    ),
  reloadRuntimeConfiguration: (projectId: string) =>
    apiFetch<{ reloaded: boolean; deploymentPublicId: string }>(
      `/projects/${projectId}/environment/reload`,
      { method: 'POST' }
    ),
  aiGrant: (projectId: string) =>
    apiFetch<{
      grant: {
        publicId: string
        period: string
        limitQuota: string
        usedQuota: string
      } | null
      credential: {
        status: string
        grantPeriod: string | null
        modelAllowlist: string[]
        lastReconciledAt: string | null
        lastError: string | null
        clientPublicId: string | null
        keyPrefix: string | null
        clientStatus: string | null
        clientExpiresAt: string | null
        clientCreatedAt: string | null
      } | null
      gatewayConfigured: boolean
      gatewayBaseUrl: string | null
      allowedModels: string[]
    }>(`/projects/${projectId}/ai-grant`),
  rotateGrantClientCredential: (projectId: string) =>
    apiFetch<{
      apiKey: string
      baseUrl: string
      expiresAt: string
      credential: {
        publicId: string
        keyPrefix: string
        status: string
        expiresAt: string
        createdAt: string
      }
    }>(`/projects/${projectId}/ai-grant/client-credential/rotate`, {
      method: 'POST',
    }),
  revokeGrantClientCredential: (projectId: string) =>
    apiFetch<{ revoked: boolean }>(
      `/projects/${projectId}/ai-grant/client-credential`,
      { method: 'DELETE' }
    ),
  usage: () =>
    apiFetch<{
      usage: {
        projects: number
        deployments: number
        readyDeployments: number
        failedDeployments: number
        sourceBytes: string
      }
    }>('/usage'),
  adminControlPlane: () =>
    apiFetch<AdminControlPlane>('/admin/control-plane'),
  updateDeveloperAccess: (
    accountId: string,
    policy: 'inherited' | 'granted' | 'revoked'
  ) =>
    apiFetch<{ account: AdminControlPlane['accounts'][number] }>(
      `/admin/accounts/${accountId}/access`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ policy }),
      }
    ),
  cancelBuildJob: (jobId: string) =>
    apiFetch<{ job: { publicId: string; state: string } }>(
      `/admin/build-jobs/${jobId}/cancel`,
      { method: 'POST' }
    ),
  updateGrantBudget: (projectId: string, limitQuota: number) =>
    apiFetch<{ grant: AdminControlPlane['grants'][number] }>(
      `/admin/projects/${projectId}/grant-budget`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ limitQuota }),
      }
    ),
  reconcileGrantCredential: (projectId: string) =>
    apiFetch<{ credential: Record<string, unknown> }>(
      `/admin/projects/${projectId}/grant-credential/reconcile`,
      { method: 'POST' }
    ),
}
